import React, { useEffect, useRef, useState } from "react";
import ChatWindow from "./ChatWindow";
import { useWhatsapp, TemplateMessagesFilters, TemplateMessageRecord } from "./WhatsappContext";

const PAGE_SIZE = 25;

interface Props {
    waId?: string;
}

const toMsgShape = (rec: TemplateMessageRecord, idx: number) => {
    const body = (rec.full_message || rec.resolved_message || rec.message || rec.template_name || rec.template_key || "Template") as string;
    return {
        _id: (rec._id || rec.id || `tpl-${idx}`).toString(),
        wa_id: (rec.recipient_phone || "").toString(),
        from: "admin",
        to: (rec.recipient_phone || "").toString(),
        type: "text" as const,
        text: body,
        message: body,
        timestamp: (rec.created_at || new Date().toISOString()) as string,
        sender: "admin" as const,
        is_read: rec.read_at || rec.status === "read" ? 1 : 0,
        is_delivered: rec.delivered_at || rec.status === "delivered" || rec.status === "read" ? 1 : 0,
        message_type: "template",
        message_id: (rec.message_id || rec._id || rec.id || `tpl-${idx}`).toString(),
        template_name: (rec.template_name || rec.template_key || "") as string,
        body,
        full_message: body,
        status: (rec.status || "") as string,
        sent_at: (rec.created_at || "") as string,
        delivered_at: rec.delivered_at,
        read_at: rec.read_at,
        failed_at: rec.failed_at,
        error_code: rec.error_code,
        error_message: rec.error_message,
        template_variables: rec.template_parameters || rec.parameters,
        provider_response: rec.provider_response,
    };
};

const TemplateMessagesView: React.FC<Props> = ({ waId }) => {
    const { templates, templateMessages, templateMessagesTotal, templateMessagesLoading, fetchTemplateMessages } = useWhatsapp();

    const [filters, setFilters] = useState<TemplateMessagesFilters>(() =>
        waId ? { recipient_phone: waId } : {}
    );
    const [offset, setOffset] = useState(0);
    const [previewImage, setPreviewImage] = useState<string | null>(null);
    const scrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const initial = waId ? { recipient_phone: waId } : {};
        setFilters(initial);
        fetchTemplateMessages(initial, PAGE_SIZE, 0);
        setOffset(0);
    }, [waId]);

    const applyFilters = (f: TemplateMessagesFilters = filters, newOffset = 0) => {
        setOffset(newOffset);
        fetchTemplateMessages(f, PAGE_SIZE, newOffset);
    };

    const clearFilters = () => {
        const reset: TemplateMessagesFilters = waId ? { recipient_phone: waId } : {};
        setFilters(reset);
        applyFilters(reset, 0);
    };

    const goToPage = (newOffset: number) => {
        applyFilters(filters, newOffset);
        scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    };

    const totalPages = Math.ceil(templateMessagesTotal / PAGE_SIZE);
    const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
    const hasActiveFilters = Object.values(filters).some(Boolean);
    const templateKeys = Object.keys(templates);

    const msgs = templateMessages.map(toMsgShape);

    return (
        <div className="flex flex-col h-full bg-slate-950">
            {/* Filter bar */}
            <div className="px-4 py-2.5 border-b border-slate-800 bg-slate-900/80 backdrop-blur-md flex flex-wrap gap-2 items-end sticky top-0 z-10">
                {templateKeys.length > 0 && (
                    <div className="flex flex-col gap-1">
                        <label className="text-[10px] text-slate-400 font-medium">Template</label>
                        <select
                            value={filters.template_key || ""}
                            onChange={e => setFilters(f => ({ ...f, template_key: e.target.value || undefined }))}
                            className="text-xs bg-slate-800 border border-slate-700 text-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-blue-500"
                        >
                            <option value="">All templates</option>
                            {templateKeys.map(k => (
                                <option key={k} value={k}>{templates[k]?.template_name || k}</option>
                            ))}
                        </select>
                    </div>
                )}
                {!waId && (
                    <div className="flex flex-col gap-1">
                        <label className="text-[10px] text-slate-400 font-medium">Phone</label>
                        <input
                            type="text"
                            placeholder="Recipient phone"
                            value={filters.recipient_phone || ""}
                            onChange={e => setFilters(f => ({ ...f, recipient_phone: e.target.value || undefined }))}
                            className="text-xs bg-slate-800 border border-slate-700 text-slate-200 rounded-lg px-2 py-1.5 placeholder-slate-500 focus:outline-none focus:border-blue-500 w-36"
                        />
                    </div>
                )}
                <div className="flex flex-col gap-1">
                    <label className="text-[10px] text-slate-400 font-medium">Status</label>
                    <select
                        value={filters.status || ""}
                        onChange={e => setFilters(f => ({ ...f, status: e.target.value || undefined }))}
                        className="text-xs bg-slate-800 border border-slate-700 text-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-blue-500"
                    >
                        <option value="">All</option>
                        <option value="sent">Sent</option>
                        <option value="delivered">Delivered</option>
                        <option value="read">Read</option>
                        <option value="failed">Failed</option>
                        <option value="pending">Pending</option>
                    </select>
                </div>
                <div className="flex flex-col gap-1">
                    <label className="text-[10px] text-slate-400 font-medium">Source</label>
                    <input
                        type="text"
                        placeholder="Source"
                        value={filters.source || ""}
                        onChange={e => setFilters(f => ({ ...f, source: e.target.value || undefined }))}
                        className="text-xs bg-slate-800 border border-slate-700 text-slate-200 rounded-lg px-2 py-1.5 placeholder-slate-500 focus:outline-none focus:border-blue-500 w-28"
                    />
                </div>
                <div className="flex gap-2 items-end">
                    <button
                        onClick={() => applyFilters()}
                        disabled={templateMessagesLoading}
                        className="text-xs px-3 py-1.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white rounded-lg transition-colors font-medium"
                    >
                        Apply
                    </button>
                    {hasActiveFilters && (
                        <button
                            onClick={clearFilters}
                            className="text-xs px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors"
                        >
                            Clear
                        </button>
                    )}
                </div>
                {templateMessagesTotal > 0 && (
                    <span className="ml-auto text-xs text-slate-500 self-center">
                        {templateMessagesTotal.toLocaleString()} total
                    </span>
                )}
            </div>

            {/* Chat window area */}
            <div
                ref={scrollRef}
                className="flex-1 overflow-y-auto px-4 py-4 sm:px-[5%] md:px-[7%] bg-slate-950 relative"
            >
                <div
                    className="absolute inset-0 opacity-[0.03] pointer-events-none"
                    style={{ backgroundImage: 'radial-gradient(#64748b 1px, transparent 1px)', backgroundSize: '24px 24px' }}
                />

                {templateMessagesLoading ? (
                    <div className="flex items-center justify-center h-40 text-slate-400 text-sm gap-2">
                        <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v3m0 12v3M3 12h3m12 0h3" />
                        </svg>
                        Loading…
                    </div>
                ) : msgs.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-40 text-slate-500 text-sm gap-2">
                        <span className="text-3xl">📭</span>
                        <span>No template messages found</span>
                    </div>
                ) : (
                    <div className="relative z-10">
                        <ChatWindow
                            messages={msgs as any}
                            previewImage={previewImage}
                            setPreviewImage={setPreviewImage}
                        />
                    </div>
                )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="px-4 py-2 border-t border-slate-800 bg-slate-900/50 flex items-center justify-between text-xs text-slate-400 shrink-0">
                    <span>Page {currentPage} of {totalPages}</span>
                    <div className="flex gap-2">
                        <button
                            disabled={offset === 0 || templateMessagesLoading}
                            onClick={() => goToPage(Math.max(0, offset - PAGE_SIZE))}
                            className="px-3 py-1 bg-slate-800 hover:bg-slate-700 disabled:opacity-40 rounded-lg transition-colors text-slate-300"
                        >
                            Prev
                        </button>
                        <button
                            disabled={currentPage >= totalPages || templateMessagesLoading}
                            onClick={() => goToPage(offset + PAGE_SIZE)}
                            className="px-3 py-1 bg-slate-800 hover:bg-slate-700 disabled:opacity-40 rounded-lg transition-colors text-slate-300"
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TemplateMessagesView;
